import { useState } from 'react';
import { Form, Input, Modal } from 'antd';
import { useIntl } from 'react-intl';

import { requiredRule } from 'forms';
import { isUserAuthorized } from 'utils/helpers';
import { ACL_MATRIX } from 'common/permissions';
import { imageUpload } from 'services/upload';
import { getRejectionSignedURL, submitRequestRejection } from 'services/wallet';
import { formatImages } from 'utils/upload';

import BRButton from 'components/BRButton/BRButton';
import { notify } from 'components/Notify/Notify';
import LoadingWrapper from 'components/LoadingWrapper/LoadingWrapper';
import UploadAttachment from 'components/SupportTickets/components/UploadAttachment/UploadAttachment';

import UploadIcon from 'assets/bosta-icons/upload-attachment-2.svg';

import './RejectRequestModal.less';

const RejectRequestModal = ({
  visible,
  close,
  method,
  onSuccess,
  requestId,
  isBostaCredits,
  ...props
}) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);

  const handleOnUploadAttachmentChange = async (fileList) => {
    if (!fileList?.length) {
      form?.current?.resetFields(['fileList', 'attachmentsTypes']);
    }

    if (fileList) {
      const formattedImage = await formatImages(fileList);

      form?.setFieldsValue({
        formattedImage: formattedImage[0]
      });
    }
  };

  const requestImageBucket = async () => {
    try {
      const { data } = await getRejectionSignedURL(requestId);
      return data;
    } catch (error) {
      notify(error.message, error);
    }
  };

  const uploadReceiptToBucket = async (formattedImage) => {
    try {
      const { url, attachmentKey } = await requestImageBucket();

      await imageUpload(url, formattedImage);
      return attachmentKey;
    } catch (error) {
      notify({ msg: error.message, error });
    }
  };

  const handleRejectRequest = async (values) => {
    try {
      setIsLoading(true);

      const { rejectionReason, formattedImage } = values;

      let payload = { rejectionReason };

      if (formattedImage?.type) {
        const attachmentKey = await uploadReceiptToBucket(formattedImage);

        payload = { ...payload, attachmentKey };
      }

      const res = await submitRequestRejection(requestId, payload);
      notify(res.message, 'success');
      onSuccess();
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setIsLoading(false);

      close();
    }
  };

  const handleSubmitForm = async () => {
    const values = await form.validateFields();
    await handleRejectRequest(values);
  };

  return (
    <Modal
      width={null}
      visible={visible}
      onCancel={close}
      closable={false}
      maskClosable={false}
      okText="Confirm"
      className="br-recharge-requests__reject-modal"
      title={
        <>
          <span>
            {intl.formatMessage({
              id: 'recharge_requests.reject_recharge_modal.title'
            })}
          </span>
        </>
      }
      footer={
        <div className="br-recharge-requests__reject-modal__footer">
          <BRButton
            type="basic"
            label={intl.formatMessage({ id: 'common.cancel' })}
            onClick={close}
          />
          <BRButton
            type="primary"
            onClick={handleSubmitForm}
            label={intl.formatMessage({
              id: 'recharge_requests.reject_recharge_modal.reject_btn'
            })}
          />
        </div>
      }
    >
      <div className="br-recharge-requests__reject-modal">
        <LoadingWrapper loading={isLoading}>
          <Form
            form={form}
            name="RejectForm"
            className="br-recharge-requests__reject-modal__form"
          >
            <Form.Item
              name="rejectionReason"
              label={
                <span className="label_sm">
                  {intl.formatMessage({
                    id: 'recharge_requests.reject_recharge_modal.reason_label'
                  })}
                </span>
              }
              rules={[
                requiredRule(
                  intl.formatMessage(
                    {
                      id: 'form.required_custom'
                    },
                    {
                      label: intl.formatMessage({
                        id: 'recharge_requests.reject_recharge_modal.reason_label'
                      })
                    }
                  )
                )
              ]}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
            {isUserAuthorized(
              [],
              [ACL_MATRIX.RECHARGE_GET_REJECTION_UPLOAD_LINK]
            ) && (
              <Form.Item
                name="formattedImage"
                label={
                  <div className="reject_recharge_modal__upload-label">
                    <span className="label_sm">
                      {intl.formatMessage({
                        id: 'recharge_requests.reject_recharge_modal.attachments'
                      })}
                    </span>
                    <span className="br-recharge-requests__reject-modal__optional ">
                      {intl.formatMessage({
                        id: 'form.optional_label'
                      })}
                    </span>
                  </div>
                }
              >
                <UploadAttachment
                  maxCount={1}
                  accept=".jpg, .jpeg, .png, .pdf"
                  customSubtitle={intl.formatMessage({
                    id: 'recharge_requests.reject_recharge_modal.upload_subtitle'
                  })}
                  maxSize={10}
                  hideUploadButtonAfterMaxCount
                  onChange={handleOnUploadAttachmentChange}
                  uploadIcon={UploadIcon}
                  directionRow={true}
                  disableEncoding
                />
              </Form.Item>
            )}
          </Form>
        </LoadingWrapper>
      </div>
    </Modal>
  );
};

export default RejectRequestModal;
